import DialogHeader from "@entrilia/shared/components/dialog/DialogHeader";
import {
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { CalculationType } from "../../../../../../../../../api/types/fieldConfigurationTypes";
import { selectAggregationFields, selectConditions } from "../../../../../store/selectors/fieldsStateSelectors";
import ConditionSelect from "./controls/ConditionSelect";
import { isConditionValueSupported } from "./controls/conditionSelectHelpers";
import { type CustomColumnData } from "./customColumn.types";
import useSetupConditionValueMenu from "./hooks/useSetupConditionValueMenu";
import { calculationTypeCapabilities } from "./utilities/calculationTypeCapabilities";

interface Props {
  fieldGuid: string | undefined;
  onClose: () => void;
  onApply: (data: CustomColumnData) => void;
  readonly: boolean;
}

const CustomColumnConditionValueSetup = ({ fieldGuid, onClose, onApply, readonly }: Props) => {
  const aggregationFields = useSelector(selectAggregationFields);
  const conditions = useSelector(selectConditions);

  const supportedConditions = useMemo(() => conditions.filter(isConditionValueSupported), [conditions]);

  const {
    isDirty,
    isValid,
    name,
    nameError,
    selectedConditionGuid,
    selectedConditionGuidError,
    onNameChange,
    onSelectedConditionGuidChange,
  } = useSetupConditionValueMenu(fieldGuid, aggregationFields, supportedConditions);

  const handleApply = () => {
    onApply({
      name,
      selectedFormulaParameterIds: [selectedConditionGuid],
      selectedCalculationType: CalculationType.ConditionValue,
      formula: "",
      format: undefined,
    });
  };

  return (
    <Stack>
      <DialogHeader text="New Calculation" onClose={onClose} />
      <Divider />
      {supportedConditions.length > 0 && (
        <Stack spacing={2} sx={{ p: 2.5 }}>
          <FormControl>
            <InputLabel id="condition-value-type-label">Type</InputLabel>
            <Select labelId="condition-value-type-label" label="Type" value={CalculationType.ConditionValue} disabled>
              <MenuItem value={CalculationType.ConditionValue}>
                {calculationTypeCapabilities[CalculationType.ConditionValue].caption}
              </MenuItem>
            </Select>
          </FormControl>
          <ConditionSelect
            selectedConditionGuid={selectedConditionGuid}
            onSelectedConditionGuidChange={onSelectedConditionGuidChange}
            conditions={supportedConditions}
            selectedConditionGuidError={selectedConditionGuidError}
            readonly={readonly}
          />
          <TextField
            autoFocus={!isDirty && isValid}
            label="Name"
            value={name}
            onChange={(evt) => onNameChange(evt.target.value)}
            error={!!nameError}
            helperText={nameError}
          />
        </Stack>
      )}
      {supportedConditions.length === 0 && (
        <Typography variant="body1" color="textSecondary" sx={{ textAlign: "center", py: 2 }}>
          No supported conditions in this report
        </Typography>
      )}
      <Divider />
      <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end", px: 2, py: 1.5 }}>
        <Button variant="text" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleApply} disabled={!isValid}>
          Apply
        </Button>
      </Stack>
    </Stack>
  );
};

export default CustomColumnConditionValueSetup;
